import { useTranslation } from "react-i18next";
import {
  requestNotificationPermissionIfNeeded,
  syncPushTokenIfPossible,
} from "../../../services/firebase";
import { iosOpenPushSettings, isIOSApp } from "../../../tools/ios";
import { androidOpenPushSettings, isAndroidApp } from "../../../tools/android";
import { displayToast } from "../../../components/app/AppToast";
import { apiClient } from "../../../services/api";
import {
  reloadCurrentUser,
  useCurrentUser,
} from "../../user/hooks/useCurrentUser";
import {
  isPushOn,
  pushSwitchAction,
  type AccountPushDecision,
} from "../utils/pushSwitchAction";
import { useNotificationPermissionState } from "./useNotificationPermissionState";
import { useNotificationForegroundSync } from "./useNotificationForegroundSync";

/** Hand the reader to the OS screen that owns the permission in a webview. */
function openNativeSettings(): void {
  if (isIOSApp()) {
    iosOpenPushSettings();
    return;
  }
  if (isAndroidApp()) {
    androidOpenPushSettings();
  }
}

export type PushNotificationSwitch = {
  /** Whether push is authorized right now. */
  readonly enabled: boolean;
  /** The OS refused. The switch stays off and says so. */
  readonly denied: boolean;
  /** The OS has never been asked, so the next tap shows its prompt. */
  readonly needsOsPrompt: boolean;
  /** In a webview, where an OS refusal is undone on the OS settings screen. */
  readonly isNative: boolean;
  readonly toggle: () => void;
  /**
   * `toggle`, awaited: resolves once every step settled, to `"on"` only when
   * push is on at the end (the browser prompt can be declined, an OS refusal
   * only explains itself, an account opt-out still holds). For a surface that
   * must not touch another channel before push has settled.
   */
  readonly toggleAndWait: () => Promise<"on" | "unchanged">;
};

/**
 * The one push switch, for every surface that draws one.
 *
 * Several outcomes hang off a single tap — ask the OS, be sent to the OS screen
 * that owns a refusal, be told where that screen is on a browser we cannot
 * deep-link into, or decide the account — and both settings surfaces had their
 * own copy of the branching, subtly different. It is a rule about the platform,
 * not about a screen. `pushSwitchAction` holds it.
 *
 * The switch answers for BOTH gates, because the reader only cares whether a
 * push arrives. The OS decides whether this device may display one; `pushOptOut`
 * decides whether the reader wants any at all. Showing the first alone left an
 * opted-out reader looking at a switch that said ON.
 */
export function usePushNotificationSwitch(): PushNotificationSwitch {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const { permissionState, isLoadingPermissionState, refreshPermissionState } =
    useNotificationPermissionState();

  useNotificationForegroundSync(
    refreshPermissionState,
    permissionState === null ? null : permissionState.status
  );

  const isNative = isIOSApp() || isAndroidApp();
  const osStatus = permissionState === null ? null : permissionState.status;
  // `currentUser === null` covers /users/me in flight, /users/me failed, and
  // signed out. None of the three is a decision, and reading them as one let a
  // tap before the account loaded record a refusal nobody made.
  const account: AccountPushDecision =
    currentUser === null
      ? { loaded: false }
      : { loaded: true, pushOptOut: currentUser.pushOptOut };
  const enabled = isPushOn(osStatus, account);
  // Reported separately from `enabled` because the copy differs: an OS refusal
  // is a state only its own settings screen can undo, while an account-level
  // opt-out is undone by this very switch.
  const denied = osStatus === "denied";

  async function request(): Promise<"on" | "unchanged"> {
    if (isLoadingPermissionState) return "unchanged";
    const action = pushSwitchAction(osStatus, account, isNative);

    if (action.kind === "wait") return "unchanged";
    if (action.kind === "open-os-settings") {
      openNativeSettings();
      return "unchanged";
    }
    if (action.kind === "explain-os-settings") {
      displayToast(t("Enable from browser settings"), "info");
      return "unchanged";
    }
    if (action.kind === "set-account") {
      try {
        await apiClient.patch("/webapp/private/users/me", {
          json: { pushOptOut: action.pushOptOut },
        });
      } catch {
        // A user action, so it is reported — unlike the background
        // reconciliation in `useFirebaseUser`. The switch keeps its position
        // because the account kept its value.
        displayToast(t("Something went wrong. Please try again."), "error");
        return "unchanged";
      }
      // The switch reads its position off the current user, so it only moves
      // once the account has actually changed.
      await reloadCurrentUser();
      if (action.pushOptOut) return "unchanged";
      await syncPushTokenIfPossible({
        force: true,
        reason: "permissionGranted",
      });
      return "on";
    }

    try {
      const granted = await requestNotificationPermissionIfNeeded();
      const nextState = await refreshPermissionState();
      if (granted || nextState.status === "authorized") {
        await syncPushTokenIfPossible({
          force: true,
          reason: "permissionGranted",
        });
        // The OS half only. An account that opted out still receives
        // nothing, which is what `isPushOn` says: the next tap writes the
        // account decision.
        return isPushOn("authorized", account) ? "on" : "unchanged";
      }
      if (isNative && nextState.mustOpenSettings) {
        openNativeSettings();
      }
      return "unchanged";
    } catch (error) {
      console.error("Failed to enable notifications:", error);
      displayToast(t("Something went wrong. Please try again."), "error");
      return "unchanged";
    }
  }

  return {
    enabled,
    denied,
    needsOsPrompt: osStatus === "notDetermined",
    isNative,
    toggle: () => {
      void request();
    },
    toggleAndWait: request,
  };
}
